import axios from 'axios';
import { apiUrl } from '@/env';
import {
  Profile,
  ProfileUpdate,
  UserCreate,
  Consumer,
  ConsumerOut,
  Scan,
  App,
  Product,
  Target,
  Version,
  AppCreate,
  ConsumerCreate,
  FilterItem,
  ScanStats,
  ConsumerStats,
  Filter,
  FilterCreate,
  CountryCluster,
  CountryClusterCreate,
  MapCluster,
  User,
  Partner,
  PartnerCreate,
  MapData,
  StatsData,
  SsoOut,
  PasswordIn,
  LoginOut,
  PasswordOut,
  ProfileUpdateOut,
  UserOut,
  PasswordRecoverIn,
} from './interfaces';

function authHeaders(token: string) {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
}

function serializeFilters(filters: FilterItem[]) {
  return JSON.stringify(
    filters.map((el) => {
      return {
        type: el.type,
        value: el.value,
      };
    }),
  );
}

export const api = {
  async login(username: string, password: string) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    return axios.post<LoginOut>(`${apiUrl}/login`, params);
  },
  async getSsoUri() {
    return axios.get<string>(`${apiUrl}/login/sso/uri`);
  },
  async loginSso(params: string) {
    return axios.get<SsoOut>(`${apiUrl}/login/sso/login${params}`);
  },
  async getMe(token: string) {
    return axios.get<Profile>(`${apiUrl}/users/me`, authHeaders(token));
  },
  async updateMe(token: string, data: ProfileUpdate) {
    return axios.put<ProfileUpdateOut>(
      `${apiUrl}/users/me`,
      data,
      authHeaders(token),
    );
  },
  async getUsers(token: string) {
    return axios.get<User[]>(`${apiUrl}/users`, authHeaders(token));
  },
  async updateUser(token: string, userId: number, data: UserCreate) {
    return axios.put<UserOut>(
      `${apiUrl}/users/${userId}`,
      data,
      authHeaders(token),
    );
  },
  async createUser(token: string, data: UserCreate) {
    return axios.post<UserOut>(`${apiUrl}/users`, data, authHeaders(token));
  },
  async exportUsers(token: string) {
    return axios.post(`${apiUrl}/users/export`, [], authHeaders(token));
  },
  async setPassword(token: string, data: PasswordIn) {
    return axios.post<PasswordOut>(
      `${apiUrl}/login/password/set`,
      data,
      authHeaders(token),
    );
  },
  async forgotPassword(token: string, data: PasswordRecoverIn) {
    return axios.post(
      `${apiUrl}/login/password/recover`,
      data,
      authHeaders(token),
    );
  },
  // TODO: need?
  async resetPassword(password: string, token: string) {
    return axios.post(`${apiUrl}/reset-password/`, {
      new_password: password,
      token,
    });
  },
  async getConsumers(
    token: string,
    appId: number,
    skip: number,
    limit: number,
    sort: string,
    desc: boolean,
  ) {
    return axios.get<Consumer[]>(
      `${apiUrl}/consumers/${appId}?skip=${skip}&limit=${limit}&sort=${sort}&desc=${desc}`,
      authHeaders(token),
    );
  },
  async exportConsumers(token: string, appId: number) {
    return axios.post(
      `${apiUrl}/consumers/${appId}/export`,
      [],
      authHeaders(token),
    );
  },
  async getConsumerStats(token: string, appId: number) {
    return axios.post<ConsumerStats[]>(
      `${apiUrl}/consumers/${appId}/stats`,
      [],
      authHeaders(token),
    );
  },
  async exportConsumerStats(token: string, appId: number) {
    return axios.post(
      `${apiUrl}/consumers/${appId}/stats/export`,
      [],
      authHeaders(token),
    );
  },
  async createConsumer(token: string, appId: number, data: ConsumerCreate) {
    return axios.post<ConsumerOut>(
      `${apiUrl}/consumers/${appId}`,
      data,
      authHeaders(token),
    );
  },
  async updateConsumer(
    token: string,
    appId: number,
    consumerId: number,
    data: ConsumerCreate,
  ) {
    return axios.put<ConsumerOut>(
      `${apiUrl}/consumers/${appId}/${consumerId}`,
      data,
      authHeaders(token),
    );
  },
  async getMyApps(token: string) {
    return axios.get<App[]>(`${apiUrl}/apps/my`, authHeaders(token));
  },
  async getApps(token: string) {
    return axios.get<App[]>(`${apiUrl}/apps`, authHeaders(token));
  },
  async createApp(token: string, data: AppCreate) {
    return axios.post(`${apiUrl}/apps`, data, authHeaders(token));
  },
  async updateApp(token: string, appId: number, data: AppCreate) {
    return axios.put(`${apiUrl}/apps/${appId}`, data, authHeaders(token));
  },
  async getUsersByApp(token: string, appId: number) {
    return axios.get<User[]>(`${apiUrl}/ul/app/${appId}`, authHeaders(token));
  },
  async getAppsByUser(token: string, userId: number) {
    return axios.get<App[]>(`${apiUrl}/ul/user/${userId}`, authHeaders(token));
  },
  async assignUsers(token: string, appId: number, data: User[]) {
    return axios.put(`${apiUrl}/ul/app/${appId}`, data, authHeaders(token));
  },
  async assignApps(token: string, userId: number, data: App[]) {
    return axios.put(`${apiUrl}/ul/user/${userId}`, data, authHeaders(token));
  },
  async getScans(
    token: string,
    appId: number,
    filters: FilterItem[],
    skip: number,
    limit: number,
    sort: string,
    desc: boolean,
  ) {
    return axios.get<Scan[]>(
      `${apiUrl}/scans/${appId}?filters=${serializeFilters(
        filters,
      )}&skip=${skip}&limit=${limit}&sort=${sort}&desc=${desc}`,
      authHeaders(token),
    );
  },
  async exportScans(token: string, appId: number, filters: FilterItem[]) {
    return axios.post(
      `${apiUrl}/scans/${appId}/export`,
      filters,
      authHeaders(token),
    );
  },
  async getScanStats(token: string, appId: number, filters: FilterItem[]) {
    return axios.post<ScanStats>(
      `${apiUrl}/scans/${appId}/stats`,
      filters,
      authHeaders(token),
    );
  },
  async exportScanStatsCsv(
    token: string,
    appId: number,
    filters: FilterItem[],
  ) {
    return axios.post(
      `${apiUrl}/scans/${appId}/stats/export/csv`,
      filters,
      authHeaders(token),
    );
  },
  async exportScanStatsXlsx(token: string, appId: number, data: StatsData) {
    var headers = authHeaders(token);
    headers['responseType'] = 'blob'; // Important for handling binary data
    return axios.post(
      `${apiUrl}/scans/${appId}/stats/export/xlsx`,
      data,
      headers,
    );
  },
  async getMapClusters(
    token: string,
    appId: number,
    filters: FilterItem[],
    bounds: string,
    zoom: number,
  ) {
    return axios.get<MapCluster[]>(
      `${apiUrl}/maps/${appId}?filters=${serializeFilters(
        filters,
      )}&bounds=${bounds}&zoom=${zoom}`,
      authHeaders(token),
    );
  },
  async getMapScans(
    token: string,
    appId: number,
    filters: FilterItem[],
    skip: number,
    limit: number,
    sort: string,
    desc: boolean,
    bounds: string,
    geo_lat: number,
    geo_lng: number,
    accuracy: number,
  ) {
    return axios.get<Scan[]>(
      `${apiUrl}/maps/${appId}/scans?filters=${serializeFilters(
        filters,
      )}&skip=${skip}&limit=${limit}&sort=${sort}&desc=${desc}&bounds=${bounds}&geo_lat=${geo_lat}&geo_lng=${geo_lng}&accuracy=${accuracy}`,
      authHeaders(token),
    );
  },
  async exportMapXlsx(token: string, appId: number, data: MapData) {
    var headers = authHeaders(token);
    headers['responseType'] = 'blob'; // Important for handling binary data
    return axios.post(`${apiUrl}/maps/${appId}/export/xlsx`, data, headers);
  },
  async getTargets(token: string, appId: number) {
    return axios.get<Target[]>(
      `${apiUrl}/targets/${appId}`,
      authHeaders(token),
    );
  },
  async getProducts(token: string, appId: number) {
    return axios.get<Product[]>(
      `${apiUrl}/products/${appId}`,
      authHeaders(token),
    );
  },
  async getVersions(token: string, appId: number) {
    return axios.get<Version[]>(
      `${apiUrl}/scans/${appId}/versions`,
      authHeaders(token),
    );
  },
  async getFilters(token: string, appId: number) {
    return axios.get<Filter[]>(`${apiUrl}/filter/${appId}`, authHeaders(token));
  },
  async createFilter(token: string, appId: number, data: FilterCreate) {
    return axios.post(`${apiUrl}/filter/${appId}`, data, authHeaders(token));
  },
  async removeFilter(token: string, appId: number, filterId: number) {
    return axios.delete(
      `${apiUrl}/filter/${appId}/${filterId}`,
      authHeaders(token),
    );
  },
  async getCountryClusters(token: string) {
    return axios.get<CountryCluster[]>(`${apiUrl}/cc`, authHeaders(token));
  },
  async createCountryCluster(token: string, data: CountryClusterCreate) {
    return axios.post(`${apiUrl}/cc`, data, authHeaders(token));
  },
  async updateCountryCluster(
    token: string,
    countryClusterId: number,
    data: CountryClusterCreate,
  ) {
    return axios.put(
      `${apiUrl}/cc/${countryClusterId}`,
      data,
      authHeaders(token),
    );
  },
  async removeCountryCluster(token: string, countryClusterId: number) {
    return axios.delete(`${apiUrl}/cc/${countryClusterId}`, authHeaders(token));
  },
  async getPartners(token: string) {
    return axios.get<Partner[]>(`${apiUrl}/partners`, authHeaders(token));
  },
  async createPartner(token: string, data: PartnerCreate) {
    return axios.post(`${apiUrl}/partners`, data, authHeaders(token));
  },
  async updatePartner(token: string, partnerId: number, data: PartnerCreate) {
    return axios.put(
      `${apiUrl}/partners/${partnerId}`,
      data,
      authHeaders(token),
    );
  },
  async getPartnersByApp(token: string, appId: number) {
    return axios.get(`${apiUrl}/pl/app/${appId}`, authHeaders(token));
  },
  async getAppsByPartner(token: string, partnerId: number) {
    return axios.get<App[]>(
      `${apiUrl}/pl/partner/${partnerId}`,
      authHeaders(token),
    );
  },
  async assignPartnerApps(token: string, partnerId: number, data: App[]) {
    return axios.put(
      `${apiUrl}/pl/partner/${partnerId}`,
      data,
      authHeaders(token),
    );
  },
};
